import React, { useEffect, useMemo } from "react";
import { graphql, useStaticQuery } from "gatsby";
import Slider from "react-slick";
import slugify from "@sindresorhus/slugify";
import CardProdotto from "./card-prodotto";
import useProductPrices from "../hooks/use-product-prices";
import sortPosition from "../utility/sort-position";
import { productImpression } from "../utility/datalayer";
import classNames from "classnames";

const settings = {
  dots: true,
  infinite: false,
  autoplay: false,
  arrows: true,
  swipe: true,
  slidesToShow: 4,
  responsive: [
    {
      breakpoint: 1200,
      settings: {
        slidesToShow: 3,
      },
    },
    {
      breakpoint: 992,
      settings: {
        slidesToShow: 2,
      },
    },
    {
      breakpoint: 576,
      settings: {
        infinite: true,
        slidesToShow: 1,
        centerMode: true,
        centerPadding: "0px",
      },
    },
  ],
};

const settingsSearch = {
  dots: true,
  infinite: false,
  autoplay: false,
  arrows: true,
  swipe: true,
  slidesToShow: 3,
  responsive: [
    {
      breakpoint: 1200,
      settings: {
        slidesToShow: 2,
      },
    },
    {
      breakpoint: 992,
      settings: {
        slidesToShow: 2,
      },
    },
    {
      breakpoint: 576,
      settings: {
        infinite: true,
        slidesToShow: 1,
        centerMode: true,
        centerPadding: "0px",
      },
    },
  ],
};

const settingsAfterSearch = {
  dots: true,
  infinite: false,
  autoplay: false,
  arrows: true,
  swipe: true,
  slidesToShow: 4,
  responsive: [
    {
      breakpoint: 1200,
      settings: {
        slidesToShow: 3,
      },
    },
    {
      breakpoint: 992,
      settings: {
        slidesToShow: 2,
      },
    },
    {
      breakpoint: 576,
      settings: {
        infinite: true,
        slidesToShow: 1,
        centerMode: true,
        centerPadding: "0px",
      },
    },
  ],
};

const ModuleSliderProdotti = ({
  module,
  products,
  priceInfo,
  config,
  origin,
  labelManagerLabels,
  location,
  isFirst,
  titleLeft,
}) => {
  const { liferayProductNotes } = useStaticQuery(graphql`
    {
      liferayProductNotes {
        ...ProductNotesFragment
      }
    }
  `);
  // Arricchisco i prodotti con di dati sui prezzi da Magento
  const prodotti = useMemo(
    () =>
      priceInfo
        ? products.map((item) => {
            const itemData = priceInfo.get(item.key_prod);
            return {
              ...item,
              msrpPrice: itemData?.msrpPrice || null,
              sellingPrice: itemData?.sellingPrice || null,
              omnibusPrice: itemData?.omnibusPrice || null,
              availableForSale: itemData?.availableForSale,
              shopLink: itemData?.shopLink || item?.shopLink || null,
              special_from_date: itemData?.special_from_date,
              special_to_date: itemData?.special_to_date,
              contributoRaee: itemData?.contributo_raee ? itemData.contributo_raee : null,
            };
          })
        : products,
    [products, priceInfo]
  );

  const prodottiNoSearch = prodotti.slice(0, 3);

  useEffect(() => {
    if (priceInfo) {
      productImpression(prodotti, origin);
    }
  }, [prodotti, origin, priceInfo]);

  const isDiscountProd = prodotti.some(
    (prod) => prod?.sellingPrice?.value && config.priceInPerc === "perc and absolute"
  );

  return (
    <>
      {module.anchorDesktop && (
        <span id={slugify(module.anchorDesktop)} className="tabs__scroll-anchor" />
      )}
      <section className="section prodotti">
        <div
          className={classNames(
            origin !== "no result page after search" && "wrapper-slider",
            origin === "no result page after search" && "wrapper-slider-suggestion-search ml-0"
          )}
        >
          <div
            className={classNames(
              !(origin === "no result page" || origin === "no result page after search") &&
                "container-fluid"
            )}
          >
            <div className="row">
              <div
                className={classNames(
                  "col-12",
                  origin === "no result page after search" && "pl-sm-3 pl-md-0"
                )}
              >
                {module?.moduleTitle && (
                  <>
                    {isFirst ? (
                      <h1
                        className={classNames(
                          "section__title mb-4",
                          origin === "no result page" && "search-no-search__suggested-title",
                          origin === "no result page after search" &&
                            "search-no-search__suggested-title",
                          titleLeft && "search-no-search__suggested-title--search"
                        )}
                      >
                        {module.moduleTitle}
                      </h1>
                    ) : (
                      <h2
                        className={classNames(
                          "section__title mb-4",
                          (origin === "no result page" || "no search recommended") &&
                            "search-no-search__suggested-title",
                          origin === "no result page after search" &&
                            "search-no-search__suggested-title ",
                          titleLeft && "search-no-search__suggested-title--search"
                        )}
                      >
                        {module.moduleTitle}
                      </h2>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="row">
              <div
                className={classNames(
                  origin !== "no result page after search" && "col-12 px-0 px-md-3",
                  origin === "no result page after search" && "col-12 pl-0"
                )}
              >
                <Slider
                  {...(origin === "no result page"
                    ? { ...settingsSearch }
                    : origin === "no result page after search"
                    ? { ...settingsAfterSearch }
                    : { ...settings })}
                  className={classNames(
                    "prodotti__slider",
                    isDiscountProd && "prodotti__slider--with-discount"
                  )}
                >
                  {(origin === "no result page" ? prodottiNoSearch : prodotti).map((item, i) => (
                    <div key={i}>
                      <CardProdotto
                        data={item}
                        config={config}
                        origin={origin}
                        labelManagerLabels={labelManagerLabels}
                        location={location}
                        productNote={liferayProductNotes}
                      />
                    </div>
                  ))}
                </Slider>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const SliderProdotti = ({
  data: { moduleTitleFieldSet },
  config,
  origin,
  labelManagerLabels,
  location,
  titleLeft,
}) => {
  const prodotti = useMemo(() => {
    // Recupero i prodotti per modulo
    return moduleTitleFieldSet.reduce((res, module, i) => {
      const products = module.products?.filter(Boolean);
      if (products?.length) {
        res.set(i, products);
      }
      return res;
    }, new Map());
  }, [moduleTitleFieldSet]);

  const sliderProductIds = useMemo(() => {
    return [...prodotti.values()].map((item) => item.map((el) => el.key_prod)).flat();
  }, [prodotti]);
  const sliderProducts = useMemo(() => {
    return [...prodotti.values()].flat();
  }, [prodotti]);
  const priceInfo = useProductPrices(sliderProductIds, sliderProducts);

  // Almeno 3 prodotti per mostrare il modulo + ordinamento posizione
  const modules = useMemo(
    () =>
      moduleTitleFieldSet.length
        ? moduleTitleFieldSet
            .filter((module) => module.products?.filter(Boolean).length >= 3)
            .sort(sortPosition)
        : moduleTitleFieldSet,
    [moduleTitleFieldSet]
  );

  return (
    <>
      {modules.map((item, i) => (
        <ModuleSliderProdotti
          module={item}
          products={prodotti.get(i)}
          priceInfo={priceInfo}
          key={i}
          config={config}
          origin={origin}
          labelManagerLabels={labelManagerLabels}
          location={location}
          titleLeft={titleLeft}
        />
      ))}
    </>
  );
};

export default SliderProdotti;
export const fragment = graphql`
  fragment FeaturedProductFragment on LiferayFeaturedProduct {
    id
    liferayFields {
      siteId
      articleId
    }
    moduleTitleFieldSet {
      moduleTitle
      anchorDesktop
      anchorMobile
      position
      idProduct
      products {
        ...BabelProdottoFragment
      }
    }
  }
`;
